import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Callout, DataListItem, Input2FA, RowsSkeletonLoader } from '@node-space/ui-components'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { useCreateContactBankAccount } from 'hooks/contacts/useCreateContactBankAccount'
import { useTwoFAContext } from 'hooks/useTwoFAContext'
import { contactActionAtom, contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import {
  getConfirmPayeeBeneficiaryParams,
  mapFormDataToBeneficiaryDetails,
} from 'pages/Contacts/utils/fiatInstruments'
import { ConfirmationOfPayeeCallout } from 'pages/Wallets/components/FiatSend/components/ConfirmationOfPayeeCallout'
import { useConfirmBeneficiaryCallout } from 'pages/Wallets/components/FiatSend/hooks/useConfirmBeneficiaryCallout'
import { ConfirmationOfPayeeCalloutTypes } from 'pages/Wallets/components/FiatSend/types'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import {
  Address,
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { ConfirmPayeeBeneficiaryParams } from 'types/payoutsV2'
import { getAddress } from 'utils/beneficiaries'
import { ContactsSheets } from '../ContactsSheets'
import { BankDetailsConfirmationCallout } from './BankDetailsConfirmationCallout'

type FiatInstrumentConfirmationProps = {
  formFieldLabels: FormFieldLabels
  formValues: ContactFiatInstrumentFormData
  isLoading: boolean
  form: UseFormReturn<ContactFiatInstrumentFormData>
}

export const FiatInstrumentConfirmation = ({
  formFieldLabels,
  formValues,
  isLoading,
  form,
}: FiatInstrumentConfirmationProps) => {
  const { t } = useTranslation()

  const { twoFactorEnabled } = useTwoFAContext()

  const contact = useAtomValue(contactAtom)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)

  const [isBankDetailsConfirmed, setIsBankDetailsConfirmed] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)

  const {
    onSubmitBankAccount,
    isLoading: isSubmittingBankAccount,
    calloutError,
  } = useCreateContactBankAccount({
    contact,
    formValues,
    form,
    onSubmit2FASuccess: () => {},
  })

  const beneficiaryDetails: ConfirmPayeeBeneficiaryParams = useMemo(() => {
    const beneficiary = mapFormDataToBeneficiaryDetails(contact, formValues)
    return getConfirmPayeeBeneficiaryParams(beneficiary)
  }, [contact, formValues])

  const { confirmPayeeData, confirmationOfPayeeCallout, isConfirmingPayee, editBeneficiary } =
    useConfirmBeneficiaryCallout({
      beneficiaryDetails,
      isPayout: false,
      isContact: true,
    })

  useEffect(() => {
    if (formValues?.twoFactorCode?.length === TWO_FA_REQUIRED_LENGTH && twoFactorEnabled) {
      onSubmitBankAccount?.()
    }
  }, [formValues?.twoFactorCode, twoFactorEnabled])

  const isSWIFT = formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isZar = formValues?.currency === BeneficiaryDetailsCurrency.ZAR
  const isAccountTypeRequired = isUsd || isZar

  const bankAddressData: Address = {
    addressLine1: formValues?.bankAddressLine1,
    addressLine2: formValues?.bankAddressLine2,
    city: formValues?.bankCity,
    postCode: formValues?.bankPostCode,
    region: formValues?.bankRegion,
    country: formValues?.bankCountryName || formValues?.bankCountryCode,
  }

  const intermediaryBankAddressData: Address = {
    addressLine1: formValues?.intermediaryBankAddressLine1,
    addressLine2: formValues?.intermediaryBankAddressLine2,
    city: formValues?.intermediaryBankCity,
    postCode: formValues?.intermediaryBankPostCode,
    region: formValues?.intermediaryBankRegion,
    country: formValues?.intermediaryBankCountryCode,
  }

  const beneficiaryAddressData: Address = {
    addressLine1: formValues?.beneficiaryAddressLine1,
    addressLine2: formValues?.beneficiaryAddressLine2,
    city: formValues?.beneficiaryCity,
    postCode: formValues?.beneficiaryPostCode,
    region: formValues?.beneficiaryRegion,
    country: formValues?.beneficiaryCountryName || formValues?.beneficiaryCountryCode,
  }

  const beneficiaryAddress = getAddress(beneficiaryAddressData)
  const bankAddress = getAddress(bankAddressData)
  const intermediaryBankAddress = getAddress(intermediaryBankAddressData)

  const handleBankDetailsConfirmationChange = (isChecked: boolean) => {
    setIsBankDetailsConfirmed(isChecked)
  }

  const handleOn2FAChange = (value: string) => {
    form.setValue('twoFactorCode', value)
  }

  const handleEditContact = useCallback(() => {
    setContactAction({
      ...contactAction,
      actionId: ContactActionIds.CONTACT,
      header: t('contacts.addContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        contactAction?.onClose?.()
      },
      meta: {
        keepUpdatedContact: true,
      },
    })
    setIsContactSheetOpen(true)
  }, [setContactAction])

  return isConfirmingPayee ? (
    <Box paddingT={24}>
      <RowsSkeletonLoader rows={6} />
    </Box>
  ) : (
    <Box width="full">
      {!!calloutError && (
        <Box paddingB={12}>
          <Callout variant="error">{calloutError}</Callout>
        </Box>
      )}
      {/* Beneficiary details */}
      <Box>
        <DataListItem
          size="large"
          id="currency"
          title={t('formFields.currency')}
          body={formValues?.currency}
          layout={'horizontal'}
        />
        <DataListItem
          size="large"
          id="description"
          title={t('formFields.beneficiaryDescription')}
          body={formValues?.description}
          layout={'horizontal'}
        />
        {beneficiaryAddress && (
          <DataListItem
            size="large"
            id="beneficiaryAddress"
            title={t('beneficiaryAddress')}
            body={beneficiaryAddress}
            layout={'horizontal'}
          />
        )}
      </Box>

      {/* Bank Details */}
      <Box>
        <DataListItem
          size="large"
          id="accountNumber"
          title={formFieldLabels?.accountNumber}
          body={formValues?.accountNumber}
          layout={'horizontal'}
        />
        <DataListItem
          size="large"
          id="bankCode"
          title={formFieldLabels?.bankCode}
          body={formValues?.bankCode}
          layout={'horizontal'}
        />
        {isAccountTypeRequired && formValues?.accountType && (
          <DataListItem
            size="large"
            id="accountType"
            title={t('formFields.accountType')}
            body={formValues?.accountType}
            layout={'horizontal'}
          />
        )}
        {formValues?.routingNumber && (
          <DataListItem
            size="large"
            id="routingNumber"
            title={t('formFields.routingNumber')}
            body={formValues?.routingNumber}
            layout={'horizontal'}
          />
        )}
        {formValues?.bankName && (
          <DataListItem
            size="large"
            id="bankCode"
            title={t('bankName')}
            body={formValues?.bankName}
            layout={'horizontal'}
          />
        )}
        {bankAddress && (
          <DataListItem
            size="large"
            id="bankAddress"
            title={t('bankAddress')}
            body={bankAddress}
            layout={'horizontal'}
          />
        )}
      </Box>
      {/* IntermediaryBankDetails */}
      {isSWIFT && formValues?.intermediaryBankCode && (
        <Box paddingT={20}>
          <h2 className="text-foreground-prominent text-h2">
            {t('manageBeneficiaries.intermediaryBankDetails')}
          </h2>
          <DataListItem
            size="large"
            id="intermediaryBankName"
            title={t('manageBeneficiaries.intermediarysBankName')}
            body={formValues?.intermediaryBankName}
            layout={'horizontal'}
          />
          <DataListItem
            size="large"
            id="intermediaryBankName"
            title={t('bicSwiftCode')}
            body={formValues?.intermediaryBankCode}
            layout={'horizontal'}
          />
          {formValues?.intermediaryBankRoutingNumber && (
            <DataListItem
              size="large"
              id="routingNumber"
              title={t('formFields.routingNumber')}
              body={formValues?.intermediaryBankRoutingNumber}
              layout={'horizontal'}
            />
          )}
          {intermediaryBankAddress && (
            <DataListItem
              size="large"
              id="bankAddress"
              title={t('intermediaryBankAddress')}
              body={intermediaryBankAddress}
              layout={'horizontal'}
            />
          )}
        </Box>
      )}

      <Box flexCol paddingT={12} gap={12}>
        {confirmationOfPayeeCallout && (
          <ConfirmationOfPayeeCallout
            type={ConfirmationOfPayeeCalloutTypes.BANK_ACCOUNT}
            confirmationOfPayeeCallout={confirmationOfPayeeCallout}
            confirmPayeeData={confirmPayeeData}
            editBeneficiary={editBeneficiary}
            handleEditBeneficiary={handleEditContact}
          />
        )}
        <BankDetailsConfirmationCallout
          onDetailsConfirmationChange={handleBankDetailsConfirmationChange}
        />
      </Box>

      <Box width="full" paddingB={12}>
        <Input2FA
          label={t('authCodeConfirm')}
          testId="add-fiat-instrument-2fa"
          disabled={!isBankDetailsConfirmed}
          loading={isLoading || isSubmittingBankAccount}
          onChange={handleOn2FAChange}
          value={formValues?.twoFactorCode}
        />
      </Box>
      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
    </Box>
  )
}
